<template>
  <div class="content-grid content-grid--subpage">
    <Breadcrumbs :title="t('routes.admin.children.activities.import.name')"/>
    <BackLinkTitle :title="t('routes.admin.children.activities.import.name')"/>
    <Card>
      <div class="form">
        <p class="form__description">{{ t('activities.import.description') }}</p>

        <FormFiles
            :key="fileInputKey"
            name="activities-import-file"
            :label="t('activities.import.fileLabel')"
            :modelValue="selectedFile"
            @update:modelValue="onFileSelected"
        />

        <FormErrorsList
            v-if="importErrors.length > 0"
            :title="t('activities.import.errorTitle')"
            :errorsList="importErrors"
        />

        <p v-if="successMessage" class="form__success">{{ successMessage }}</p>

        <div class="form__actions">
          <button
              type="button"
              class="btn"
              :disabled="!selectedFile || isImporting"
              @click="submit"
          >
            {{ isImporting ? t('activities.import.importing') : t('activities.import.submit') }}
          </button>
        </div>
      </div>
    </Card>
  </div>
</template>

<script lang="ts" setup>
import Card from '@/components/layout/Card.vue';
import BackLinkTitle from '@/components/layout/BackLinkTitle.vue';
import Breadcrumbs from '@/components/layout/Breadcrumbs.vue';
import FormErrorsList from '@/components/forms/FormErrorsList.vue';
import FormFiles from '@/components/forms/FormFiles.vue';
import {useI18n} from "vue3-i18n";
import {ref} from "vue";
import {useActivityService} from "@/inversify.config";
import {notifyError, notifySuccess} from "@/notify";

const {t} = useI18n();
const activityService = useActivityService();

const selectedFile = ref<File | null>(null);
const fileInputKey = ref(0);
const isImporting = ref(false);
const importErrors = ref<string[]>([]);
const successMessage = ref("");

function onFileSelected(files: File | Array<File> | undefined) {
  const file = Array.isArray(files) ? files[0] : files;
  selectedFile.value = file ?? null;
  importErrors.value = [];
  successMessage.value = "";
}

async function submit() {
  if (!selectedFile.value) {
    notifyError(t('activities.import.noFile'));
    return;
  }

  isImporting.value = true;
  importErrors.value = [];
  successMessage.value = "";

  try {
    const result = await activityService.importActivities(selectedFile.value);
    if (result?.success) {
      successMessage.value = t('activities.import.success', {count: result.importedCount});
      notifySuccess(t('activities.import.successTitle'));
      // Reset the file picker so an accidental second click cannot re-import the same activities.
      selectedFile.value = null;
      fileInputKey.value++;
    } else {
      const errors = (result?.errors ?? []).map(error => error.description);
      importErrors.value = errors.length > 0 ? errors : [t('activities.import.genericError')];
      notifyError(t('activities.import.errorTitle'));
    }
  } catch {
    importErrors.value = [t('activities.import.genericError')];
    notifyError(t('activities.import.genericError'));
  } finally {
    isImporting.value = false;
  }
}
</script>
